import React from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import { useProcurationContext } from 'src/Contexts/ProcurationContext';
import FormSection from 'src/Components/FormFields/FormSection';
// import { BrandButton } from 'src/Components/shared';
import LayoutProcuration from 'src/Components/Procuration/LayoutProcuration';
import ButtonField from 'src/Components/Ui/ButtonField';
import { listeNationalities } from 'src/Hooks/useProcuration';

const SixthPartAe = () => {
    const { classes } = useTailwindBrand();
    const context = useProcurationContext();
    const { prevStep, nextStep, generalProcuration } = context;
    const [expanded, setExpanded] = React.useState<string | false>('');

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const formatYesNo = (value: number): string => {
        if (value === 1) return 'Oui';
        if (value === 2) return 'Non';
        return 'Non renseigné';
    };

    const formatGender = (value: number): string => {
        if (value === 1) return 'Femme';
        if (value === 2) return 'Homme';
        return 'Non renseigné';
    };

    const formatMaritalStatus = (value: number): string => {
        const statuses = ['', 'Célibataire', 'Marié(e)', 'Divorcé(e)', 'Veuf(ve)', 'Pacsé(e)', 'En concubinage'];
        return statuses[value] || 'Non renseigné';
    };

    const formatHealthInsurance = (value: number): string => {
        const insurances = ['', 'Agricole', 'Non salarié', 'ENIM', 'Non agricole', 'Régime Général', 'Autre'];
        return insurances[value] || 'Non renseigné';
    };

    const formatDate = (date: string | Date): string => {
        if (!date) return 'Non renseigné';
        return moment(date).format('DD/MM/YYYY');
    };

    const handleEdit = (step: number) => {
        nextStep(step);
    };

    // Données personnelles (Étape 1)
    const personalData = generalProcuration?.secondProc || {};
    // Données document d'identité (Étape 2)
    const identityDocData = generalProcuration?.thirdProc || {};
    // Données sécurité sociale (Étape 3)
    const socialSecurityData = generalProcuration?.fourthProc || {};
    // Données configuration micro-entreprise (Étape 4)
    const microEnterpriseConfigData = generalProcuration?.fifthProc || {};
    // Données options micro-entreprise (Étape 5)
    const microEnterpriseOptionsData = generalProcuration?.sixthProc || {};
    // Données autorisations (Étape 6)
    const authorizationsData = generalProcuration?.seventhProc || {};
    // Représentants légaux
    const legalRepresentatives = generalProcuration?.legalRepProc || [];

    const handleNext = () => {
        nextStep(8);
    };

    return (
        <LayoutProcuration
            title="Récapitulatif de votre procuration"
            icon={<DescriptionIcon className={`w-6 h-6 ${classes.primaryText}`} />}
            nextStep={handleNext}
            prevStep={prevStep}
        >
            <FormSection title="Vérification des informations">
                <div className="space-y-4">
                    <p className={`md:text-sm text-xs font-semibold ${classes.text} ${classes.fontFamily} mb-6`}>
                        Veuillez vérifier l'ensemble des informations saisies avant de continuer. Vous pouvez modifier chaque section en cliquant sur "Modifier".
                    </p>

                    {/* Étape 1 : Informations personnelles */}
                    <Accordion
                        expanded={expanded === 'panel1'}
                        onChange={handleChange('panel1')}
                        className="/border /border-gray-200 rounded-md shadow-md"
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.primaryText} />}
                            className="bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 w-full">
                                <PersonIcon className={`w-5 h-5 ${classes.primaryText}`} />
                                <div className="flex-1">
                                    <h3 className={`${classes.fontFamily} font-bold text-lg ${classes.primaryText}`}>
                                        Étape 1 : Informations personnelles
                                    </h3>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} opacity-70`}>
                                        {personalData.firstName && personalData.lastName ? `${personalData.firstName} ${personalData.lastName}` : 'Informations à compléter'}
                                    </p>
                                </div>
                                {personalData.firstName && personalData.lastName && (
                                    <CheckCircleIcon className="text-green-500 w-5 h-5" />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className="bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Prénom</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.firstName || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Nom de naissance</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.lastName || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Date de naissance</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatDate(personalData.birthDate)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Ville de naissance</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.birthCity || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Pays de naissance</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.country || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Genre</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatGender(personalData.gender)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Nationalité</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{listeNationalities[personalData.nationality - 1] || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Situation matrimoniale</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatMaritalStatus(personalData.maritalStatus)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Téléphone</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.phone || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Adresse</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.address || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Procuration faite à</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{personalData.signCity || 'Non renseigné'}</p>
                                </div>
                            </div>
                            <div className="flex justify-end pt-4 border-t /border-gray-200 mt-4">
                                {/* <BrandButton
                                    onClick={() => handleEdit(1)}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    <EditIcon className="w-4 h-4" />
                                    Modifier
                                </BrandButton> */}
                                <ButtonField
                                    label={"Modifier"}
                                    variant="outlined"
                                    className={`w-full font-bold lg:w-auto`}
                                    onClick={() => handleEdit(1)}
                                />
                            </div>
                        </AccordionDetails>
                    </Accordion>

                    {/* Étape 2 : Document d'identité */}
                    <Accordion
                        expanded={expanded === 'panel2'}
                        onChange={handleChange('panel2')}
                        className="/border /border-gray-200 rounded-md shadow-md"
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.primaryText} />}
                            className="bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 w-full">
                                <AttachFileIcon className={`w-5 h-5 ${classes.primaryText}`} />
                                <div className="flex-1">
                                    <h3 className={`${classes.fontFamily} font-bold text-lg ${classes.primaryText}`}>
                                        Étape 2 : Document d'identité
                                    </h3>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} opacity-70`}>
                                        {identityDocData.file ? 'Document d\'identité téléchargé' : 'Aucun document téléchargé'}
                                    </p>
                                </div>
                                {identityDocData.file && (
                                    <CheckCircleIcon className="text-green-500 w-5 h-5" />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className="bg-white">
                            <div className="py-4">
                                {identityDocData.file ? (
                                    <div className="space-y-3">
                                        <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily} mb-4`}>
                                            Votre document d'identité a été téléchargé avec succès.
                                        </p>
                                        <div className="flex flex-col gap-2">
                                            <a
                                                href={identityDocData.file.contentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`inline-flex items-center gap-2 ${classes.primaryText} font-semibold hover:underline transition-colors`}
                                            >
                                                <DescriptionIcon className="w-5 h-5" />
                                                Voir le document d'identité
                                            </a>
                                            {identityDocData.file2 && (
                                                <a
                                                    href={identityDocData.file2.contentUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`inline-flex items-center gap-2 ${classes.primaryText} font-semibold hover:underline transition-colors`}
                                                >
                                                    <DescriptionIcon className="w-5 h-5" />
                                                    Voir le document supplémentaire
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily} text-orange-600`}>
                                        Aucun document d'identité n'a été téléchargé.
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-end pt-4 border-t /border-gray-200 mt-4">
                                {/* <BrandButton
                                    onClick={() => handleEdit(2)}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    <EditIcon className="w-4 h-4" />
                                    Modifier
                                </BrandButton> */}
                                <ButtonField
                                    label={"Modifier"}
                                    variant="outlined"
                                    className={`w-full font-bold lg:w-auto`}
                                    onClick={() => handleEdit(2)}
                                />
                            </div>
                        </AccordionDetails>
                    </Accordion>

                    {/* Étape 3 : Sécurité sociale */}
                    <Accordion
                        expanded={expanded === 'panel3'}
                        onChange={handleChange('panel3')}
                        className="/border /border-gray-200 rounded-md shadow-md"
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.primaryText} />}
                            className="bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 w-full">
                                <SecurityIcon className={`w-5 h-5 ${classes.primaryText}`} />
                                <div className="flex-1">
                                    <h3 className={`${classes.fontFamily} font-bold text-lg ${classes.primaryText}`}>
                                        Étape 3 : Sécurité sociale
                                    </h3>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} opacity-70`}>
                                        {socialSecurityData.socialSecurityNumber ? 'Informations complétées' : 'Informations à compléter'}
                                    </p>
                                </div>
                                {socialSecurityData.socialSecurityNumber && (
                                    <CheckCircleIcon className="text-green-500 w-5 h-5" />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className="bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Avez-vous exercé une activité non salariée en France ?</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(socialSecurityData.hasActivity)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Avez-vous déjà eu le statut auto-entrepreneur par le passé ?</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(socialSecurityData.isAe)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Numéro de sécurité sociale</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{socialSecurityData.socialSecurityNumber || 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Organisme d'assurance maladie</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>
                                        {formatHealthInsurance(socialSecurityData.healthInsurance)}
                                        {socialSecurityData.healthInsurance === 6 && socialSecurityData.healthInsuranceOther && ` - ${socialSecurityData.healthInsuranceOther}`}
                                    </p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Mode de versement des cotisations sociales</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>
                                        {socialSecurityData.socialTaxMode === 1 ? 'Mensuel' : socialSecurityData.socialTaxMode === 2 ? 'Trimestriel' : 'Non renseigné'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end pt-4 border-t /border-gray-200 mt-4">
                                {/* <BrandButton
                                    onClick={() => handleEdit(3)}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    <EditIcon className="w-4 h-4" />
                                    Modifier
                                </BrandButton> */}
                                <ButtonField
                                    label={"Modifier"}
                                    variant="outlined"
                                    className={`w-full font-bold lg:w-auto`}
                                    onClick={() => handleEdit(3)}
                                />
                            </div>
                        </AccordionDetails>
                    </Accordion>

                    {/* Étape 4 : Configuration micro-entreprise */}
                    <Accordion
                        expanded={expanded === 'panel4'}
                        onChange={handleChange('panel4')}
                        className="/border /border-gray-200 rounded-md shadow-md"
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.primaryText} />}
                            className="bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 w-full">
                                <BusinessIcon className={`w-5 h-5 ${classes.primaryText}`} />
                                <div className="flex-1">
                                    <h3 className={`${classes.fontFamily} font-bold text-lg ${classes.primaryText}`}>
                                        Étape 4 : Configuration de votre micro-entreprise
                                    </h3>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} opacity-70`}>
                                        {microEnterpriseConfigData.otherActivities || microEnterpriseConfigData.fixAddressAtHome ? 'Informations complétées' : 'Informations à compléter'}
                                    </p>
                                </div>
                                {(microEnterpriseConfigData.otherActivities || microEnterpriseConfigData.fixAddressAtHome) && (
                                    <CheckCircleIcon className="text-green-500 w-5 h-5" />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className="bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Autres activités de service à la personne</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(microEnterpriseConfigData.otherActivities)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Adresse fixée au domicile</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(microEnterpriseConfigData.fixAddressAtHome)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Recours à une société de domiciliation</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(microEnterpriseConfigData.useDomiciliationCompany)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Ajout de représentants légaux</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(microEnterpriseOptionsData.addOtherLegalRepresentatives)}</p>
                                </div>
                                {legalRepresentatives && legalRepresentatives.length > 0 && (
                                    <div className="md:col-span-2">
                                        <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-2`}>Représentants légaux ajoutés</p>
                                        <div className="space-y-2">
                                            {legalRepresentatives.map((rep: any, index: number) => (
                                                <div key={index} className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily} font-semibold`}>
                                                        {rep.firstName} {rep.lastName}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end pt-4 border-t /border-gray-200 mt-4">
                                {/* <BrandButton
                                    onClick={() => handleEdit(4)}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    <EditIcon className="w-4 h-4" />
                                    Modifier
                                </BrandButton> */}
                                <ButtonField
                                    label={"Modifier"}
                                    variant="outlined"
                                    className={`w-full font-bold lg:w-auto`}
                                    onClick={() => handleEdit(4)}
                                />
                            </div>
                        </AccordionDetails>
                    </Accordion>

                    {/* Étape 5 : Options micro-entreprise */}
                    <Accordion
                        expanded={expanded === 'panel5'}
                        onChange={handleChange('panel5')}
                        className="/border /border-gray-200 rounded-md shadow-md"
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.primaryText} />}
                            className="bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 w-full">
                                <SettingsIcon className={`w-5 h-5 ${classes.primaryText}`} />
                                <div className="flex-1">
                                    <h3 className={`${classes.fontFamily} font-bold text-lg ${classes.primaryText}`}>
                                        Étape 5 : Options de votre micro-entreprise
                                    </h3>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} opacity-70`}>
                                        {microEnterpriseOptionsData.renounceInsaisissability || microEnterpriseConfigData.optForLiberatoryPayment ? 'Informations complétées' : 'Informations à compléter'}
                                    </p>
                                </div>
                                {(microEnterpriseOptionsData.renounceInsaisissability || microEnterpriseConfigData.optForLiberatoryPayment) && (
                                    <CheckCircleIcon className="text-green-500 w-5 h-5" />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className="bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Renonciation à l'insaisissabilité</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(microEnterpriseOptionsData.renounceInsaisissability)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Nom commercial</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>
                                        {microEnterpriseOptionsData.addCommercialName === 1 ? (microEnterpriseOptionsData.commercialNameValue || 'Oui (non renseigné)') : formatYesNo(microEnterpriseOptionsData.addCommercialName)}
                                    </p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Employez-vous des salariés ?</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>
                                        {microEnterpriseOptionsData.employEmployees === 1 ? `${microEnterpriseOptionsData.employeeCount || 'Oui'} salarié(s)` : formatYesNo(microEnterpriseOptionsData.employEmployees)}
                                    </p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Versement libératoire</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(microEnterpriseConfigData.optForLiberatoryPayment)}</p>
                                </div>
                            </div>
                            <div className="flex justify-end pt-4 border-t /border-gray-200 mt-4">
                                {/* <BrandButton
                                    onClick={() => handleEdit(5)}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    <EditIcon className="w-4 h-4" />
                                    Modifier
                                </BrandButton> */}
                                <ButtonField
                                    label={"Modifier"}
                                    variant="outlined"
                                    className={`w-full font-bold lg:w-auto`}
                                    onClick={() => handleEdit(5)}
                                />
                            </div>
                        </AccordionDetails>
                    </Accordion>

                    {/* Étape 6 : Autorisations */}
                    <Accordion
                        expanded={expanded === 'panel6'}
                        onChange={handleChange('panel6')}
                        className="/border /border-gray-200 rounded-md shadow-md"
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.primaryText} />}
                            className="bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3 w-full">
                                <SecurityIcon className={`w-5 h-5 ${classes.primaryText}`} />
                                <div className="flex-1">
                                    <h3 className={`${classes.fontFamily} font-bold text-lg ${classes.primaryText}`}>
                                        Étape 6 : Autorisations et consentements
                                    </h3>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} opacity-70`}>
                                        {authorizationsData.authorizeSirenDirectoryAccess || authorizationsData.addDomainName ? 'Informations complétées' : 'Informations à compléter'}
                                    </p>
                                </div>
                                {(authorizationsData.authorizeSirenDirectoryAccess || authorizationsData.addDomainName) && (
                                    <CheckCircleIcon className="text-green-500 w-5 h-5" />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className="bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Autorisation SIREN</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(authorizationsData.authorizeSirenDirectoryAccess)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Autorisation prospection</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>{formatYesNo(authorizationsData.authorizePersonalDataProspecting)}</p>
                                </div>
                                <div>
                                    <p className={`text-sm font-semibold ${classes.text} ${classes.fontFamily} mb-1`}>Nom de domaine</p>
                                    <p className={`md:text-sm text-xs ${classes.text} ${classes.fontFamily}`}>
                                        {authorizationsData.addDomainName === 1 ? (authorizationsData.domainNameValue || 'Oui (non renseigné)') : formatYesNo(authorizationsData.addDomainName)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end pt-4 border-t /border-gray-200 mt-4">
                                {/* <BrandButton
                                    onClick={() => handleEdit(6)}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    <EditIcon className="w-4 h-4" />
                                    Modifier
                                </BrandButton> */}
                                <ButtonField
                                    label={"Modifier"}
                                    variant="outlined"
                                    className={`w-full font-bold lg:w-auto`}
                                    onClick={() => handleEdit(6)}
                                />
                            </div>
                        </AccordionDetails>
                    </Accordion>

                </div>
            </FormSection>
        </LayoutProcuration>
    );
};

export default SixthPartAe;
